'use client'

import { useQuery } from '@tanstack/react-query'
import { authFetchJson } from '@/shared/lib/auth-fetch'

export type RegionOption = { value: string; label: string }

const REGIONS_STALE_MS = 5 * 60 * 1000 // 5 minutes – regions change rarely

async function fetchRegionOptions(activeOnly: boolean): Promise<RegionOption[]> {
  const url = activeOnly
    ? '/regions/options?activeOnly=true'
    : '/regions/options'
  const res = await authFetchJson<any>(url)
  if (!res.success) throw new Error(res.message || 'Failed to fetch regions')
  const data = res.data?.data ?? res.data
  const regions = Array.isArray(data) ? data : []
  return regions.map((r: { id: string | number; label?: string; name?: string }) => ({
    value: String(r.id),
    label: r.label ?? r.name ?? String(r.id),
  }))
}

/**
 * Fetches region options for dropdowns with caching.
 * Uses React Query so the same request is shared and cached across Profile, Update Volunteer, and Add/Edit Volunteer forms.
 */
export function useRegionOptions(activeOnly: boolean = true) {
  const query = useQuery({
    queryKey: ['regions', 'options', activeOnly],
    queryFn: () => fetchRegionOptions(activeOnly),
    staleTime: REGIONS_STALE_MS,
  })

  const options: RegionOption[] = Array.isArray(query.data) ? query.data : []
  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
